import { useCallback, useEffect, useRef, useState } from "react";
import type { CreateGameFormProps } from "../../game-registry";

type YachtTurnModeConfig = "auto" | "SEQUENTIAL" | "SIMULTANEOUS";

interface YachtCreateGameFormVariantProps extends CreateGameFormProps {
  readonly turnMode: Exclude<YachtTurnModeConfig, "auto">;
  readonly title: string;
  readonly description: string;
}

function YachtCreateGameFormVariant({
  onConfigChange,
  turnMode,
  title,
  description,
}: YachtCreateGameFormVariantProps) {
  const [seed, setSeed] = useState("");
  const onConfigChangeRef = useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;

  useEffect(() => {
    onConfigChangeRef.current({ turnMode });
  }, [turnMode]);

  const emitSeedConfig = useCallback(
    (nextSeed: string) => {
      const trimmedSeed = nextSeed.trim();
      onConfigChange({
        turnMode,
        ...(trimmedSeed.length > 0 ? { seed: trimmedSeed } : {}),
      });
    },
    [onConfigChange, turnMode],
  );

  const showSeedField =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  return (
    <section className="rounded-md border border-border-subtle bg-surface-secondary p-3">
      <div className="mb-3">
        <div className="font-semibold text-sm text-text-primary">{title}</div>
        <div className="mt-1 text-text-muted text-xs">{description}</div>
      </div>
      <div className="grid gap-3">
        <div
          className="rounded-md border border-border-subtle bg-surface-primary px-3 py-2 font-semibold text-text-secondary text-xs"
          data-testid="yacht-fixed-turn-mode"
        >
          {turnMode === "SEQUENTIAL" ? "턴제 플레이" : "동시턴 플레이"}
        </div>
        {showSeedField && (
          <label className="grid gap-1.5 text-sm">
            <span className="font-semibold text-text-secondary">
              테스트 seed
            </span>
            <input
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 text-text-primary"
              data-testid="yacht-seed-input"
              placeholder="비우면 무작위"
              type="text"
              value={seed}
              onChange={(event) => {
                setSeed(event.currentTarget.value);
                emitSeedConfig(event.currentTarget.value);
              }}
            />
          </label>
        )}
      </div>
    </section>
  );
}

export function YachtCreateGameForm(props: CreateGameFormProps) {
  return (
    <YachtCreateGameFormVariant
      {...props}
      turnMode="SEQUENTIAL"
      title="Yacht 설정"
      description="2~6명이 한 명씩 차례로 주사위를 굴리는 턴제 게임입니다."
    />
  );
}

export function YachtRaceCreateGameForm(props: CreateGameFormProps) {
  return (
    <YachtCreateGameFormVariant
      {...props}
      turnMode="SIMULTANEOUS"
      title="Yacht Race 설정"
      description="4~12명이 같은 라운드 안에서 각자 턴을 완료하는 동시턴 게임입니다."
    />
  );
}
